<template>
  <div class="bg-white p-6 rounded-lg shadow-lg">
    <h3 class="text-xl font-bold mb-4 text-center">Upload CPD Tokens</h3>
    
    <div class="space-y-6">
      <div v-for="(row, index) in registrationBodiesForm" :key="index" class="border rounded p-4">
        <div class="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
          <div>
            <label class="block text-sm font-bold mb-1">Professional Body</label>
            <select
              v-model="row.bodyId"
              class="w-full border border-gray-300 rounded px-3 py-2 bg-gray-50"
              :disabled="loadingProfessionalBodies"
            >
              <option value="" disabled selected>
                {{ loadingProfessionalBodies ? 'Loading...' : 'Select' }}
              </option>
              <option v-for="body in professionalBodies" :key="body.id" :value="body.id">
                {{ body.name }}
              </option>
            </select>
          </div>
          <div>
            <label class="block text-sm font-bold mb-1">CPD Points</label>
            <input
              type="number"
              v-model.number="row.points"
              min="1"
              class="w-full border border-gray-300 rounded px-3 py-2 bg-gray-50"
              placeholder="Enter CPD points"
            />
          </div>
          <div>
            <label class="block text-sm font-bold mb-1">Upload CPD Tokens</label>
            <div class="flex items-center space-x-2">
              <div class="flex-1 border-2 border-dashed border-gray-300 rounded p-2 bg-gray-50 flex items-center justify-between">
                <label :for="`token-file-${index}`" class="cursor-pointer px-3 py-1 bg-gray-100 hover:bg-gray-200 rounded text-sm">Choose File</label>
                <input
                  :id="`token-file-${index}`"
                  type="file"
                  accept=".xls,.xlsx,.csv"
                  class="hidden"
                  @change="handleBodyFileChange($event, index)"
                />
                <span class="text-xs text-gray-600 truncate ml-2">{{ row.file ? row.file.name : 'No file selected' }}</span>
              </div>
              <button
                v-if="row.file"
                @click="removeRegistrationBodyFile(index)"
                class="w-8 h-8 bg-red-500 text-white rounded flex items-center justify-center hover:bg-red-600"
                title="Remove file"
              >
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
                </svg>
              </button>
            </div>
          </div>
        </div>
        <div class="mt-3 flex justify-end space-x-2">
          <button
            v-if="index === registrationBodiesForm.length - 1"
            @click="addRegistrationBody"
            class="px-3 py-1 bg-orange-500 hover:bg-orange-600 text-white rounded text-sm"
          >
            Add Row
          </button>
          <button
            v-if="registrationBodiesForm.length > 1"
            @click="removeRegistrationBody(index)"
            class="px-3 py-1 bg-gray-200 hover:bg-gray-300 text-gray-800 rounded text-sm"
          >
            Remove Row
          </button>
        </div>
      </div>
    </div>

    <div class="flex justify-between mt-6">
      <button
        v-if="!standalone"
        @click="$emit('previous')"
        class="bg-gray-300 hover:bg-gray-400 text-gray-800 font-bold py-2 px-4 rounded"
      >
        Previous
      </button>
      <button
        v-if="standalone"
        @click="$emit('close')"
        class="bg-gray-300 hover:bg-gray-400 text-gray-800 font-bold py-2 px-4 rounded"
      >
        Cancel
      </button>
      <button
        @click="handleUploadTokens"
        :disabled="uploading"
        class="bg-custom-blue hover:bg-blue-600 text-white font-bold py-2 px-4 rounded flex items-center gap-2"
      >
        {{ uploading ? 'Uploading...' : (standalone ? 'Upload' : 'Upload Tokens') }}
        <svg v-if="uploading" class="w-4 h-4 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
      </button>
    </div>
  </div>
</template>

<script>
import { ref, onMounted } from 'vue';
import { useToast } from 'vue-toastification';
import api from '@/services/api';

export default {
  name: 'UploadTokensForm',
  emits: ['previous', 'next', 'upload-complete', 'close'],
  props: {
    assessmentId: {
      type: [Number, String],
      required: false,
      default: null
    },
    standalone: {
      type: Boolean,
      default: false
    }
  },
  setup(props, { emit }) {
    const toast = useToast();
    const professionalBodies = ref([]);
    const loadingProfessionalBodies = ref(false);
    const registrationBodiesForm = ref([
      { bodyId: '', points: 0, file: null }
    ]);
    const uploading = ref(false);

    const fetchProfessionalBodies = async () => {
      loadingProfessionalBodies.value = true;
      try {
        console.log('Fetching professional bodies');
        const response = await api.getProfessionalBodies();
        console.log('Raw professional bodies response:', response);
        
        // Handle the nested structure: { "professional_bodies": [...] }
        if (response && response.professional_bodies) {
          professionalBodies.value = response.professional_bodies;
        } else if (response && response.data && response.data.professional_bodies) {
          professionalBodies.value = response.data.professional_bodies;
        } else if (Array.isArray(response.data)) {
          professionalBodies.value = response.data;
        } else if (Array.isArray(response)) {
          professionalBodies.value = response;
        } else {
          professionalBodies.value = [];
        }
        
        console.log('Parsed professional bodies:', professionalBodies.value);
        if (professionalBodies.value.length === 0) {
          console.warn('No professional bodies found');
        }
      } catch (e) {
        console.error('Error fetching professional bodies', e);
        toast.error('Error fetching professional bodies');
        professionalBodies.value = [];
      } finally {
        loadingProfessionalBodies.value = false;
      }
    };

    const handleBodyFileChange = async (e, index) => {
      const target = e.target;
      if (!target.files?.length) {
        registrationBodiesForm.value[index].file = null;
        return;
      }
      const file = target.files[0];
      const name = file.name.toLowerCase();
      if (!(name.endsWith('.xls') || name.endsWith('.xlsx') || name.endsWith('.csv'))) {
        toast.error('Please upload a valid Excel (.xls, .xlsx) or CSV (.csv) file');
        target.value = '';
        registrationBodiesForm.value[index].file = null;
        return;
      }
      registrationBodiesForm.value[index].file = file;
      toast.success(`File "${file.name}" selected successfully`);
    };

    const addRegistrationBody = () => {
      registrationBodiesForm.value.push({ bodyId: '', points: 0, file: null });
    };

    const removeRegistrationBody = (index) => {
      registrationBodiesForm.value.splice(index, 1);
    };

    const removeRegistrationBodyFile = (index) => {
      registrationBodiesForm.value[index].file = null;
      toast.success('File removed successfully');
    };

    const handleUploadTokens = async () => {
      uploading.value = true;
      if (!props.standalone && !props.assessmentId) {
        toast.warning('Please upload an assessment first.');
        uploading.value = false;
        return;
      }

      // Validate rows: at least one valid row
      const validRows = registrationBodiesForm.value.filter(r => r.bodyId && r.points > 0 && r.file);
      if (validRows.length === 0) {
        toast.warning('Please add at least one complete row: body, CPD points, and file.');
        uploading.value = false;
        return;
      }

      // Create FormData for file uploads
      const formData = new FormData();
      
      // Add assessment_id
      formData.append('assessment_id', props.assessmentId);
      
      // Get unique professional body IDs
      const uniqueBodyIds = [...new Set(validRows.map(r => r.bodyId))];
      
      // Add files in order
      validRows.forEach(r => {
        formData.append('files[]', r.file);
      });
      
      // Add unique professional body IDs
      uniqueBodyIds.forEach(bodyId => {
        formData.append('professional_body_ids[]', bodyId);
      });
      
      // Add token_attributes as JSON string
      const tokenAttributes = {};
      validRows.forEach(r => {
        tokenAttributes[r.bodyId] = {
          cpd_points: r.points
        };
      });
      formData.append('token_attributes', JSON.stringify(tokenAttributes));

      try {
        console.log('Submitting course tokens with FormData:');
        console.log('Assessment ID:', props.assessmentId);
        console.log('Valid rows:', validRows);
        console.log('Unique body IDs:', uniqueBodyIds);
        console.log('Token attributes:', tokenAttributes);
        
        // Debug FormData contents
        for (let [key, value] of formData.entries()) {
          console.log(`${key}:`, value);
        }
        
        const response = await api.addCourseTokens(formData);
        console.log('Course tokens response:', response);
        toast.success('Tokens uploaded successfully!');
        
        if (props.standalone) {
          emit('close');
        } else {
          emit('upload-complete');
        }
      } catch (error) {
        console.error('Error uploading course tokens:', error);
        toast.error(error.message || 'An unexpected error occurred during upload.');
      } finally {
        uploading.value = false;
      }
    };

    onMounted(async () => {
      await fetchProfessionalBodies();
    });

    return {
      professionalBodies,
      loadingProfessionalBodies,
      registrationBodiesForm,
      uploading,
      handleBodyFileChange,
      addRegistrationBody,
      removeRegistrationBody,
      removeRegistrationBodyFile,
      handleUploadTokens
    };
  }
};
</script>
